import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Col, Card, Row, Form, InputGroup, Button, Alert } from 'react-bootstrap';
import Datetime from 'react-datetime';
import { NavLink } from 'react-router-dom';
import CrewEqGroupList from './CrewEqGroupList';
import { getPlanTypeList } from '../../actions/farm';
import { getWorkPlan, workPlan, getCrewInfo, crewInfo, createCrew, editCrew } from '../../actions/crew';
import { getCrewEquipmentGroupList, crewEquipmentGroupList } from '../../actions/equipment-group';
import { FormValidator } from '../../helpers/form-validator';
import { toMoment, toApiDateString, getDefaultMoment } from '../../helpers/moment-ex';
import { getMatchParams } from '../../helpers/common';
import { Unit, roundOfDays } from '../../helpers/unit';
import CrewPlanType from './CrewPlanType';
import { DISPLAY_DATE_FORMAT } from '../../constants';
import { CardContainer, Validator, Heading5, TitleIcon } from '../widgets';

class CrewForm extends React.Component {
    constructor() {
        super();
        this.unit = Unit.getInstance();
        this.validator = new FormValidator(this.getRules());
        this.submitted = false;
        this.isEditMode = false;
        this.returnUrl = "";
        this.eqGroupIdWiseList = {};
        this.prevPlanned = null;

        this.clearPlanned = {
            plannedAcres: 0,
            utilization: 0,
            avgWorkingSpeed: 0,
            workingHoursPerDay: 0,
            nonWorkingDays: 0
        }

        this.defaultPlanned = {
            plannedAcres: 1,
            utilization: 40,
            avgWorkingSpeed: 4,
            workingHoursPerDay: 10,
            nonWorkingDays: 0
        }

        this.state = {
            ...this.defaultPlanned,
            validation: this.validator.valid(),
            validateGroup: null,
            farmId: 0,
            planTypeId: 0,
            crewId: 0,
            crewName: "",
            startDate: null,
            revisionDate: "",
            selectedEqGroups: [],
            isNoWorkPlan: false,
            isValidateAll: false,
            searchText: ''
        }

        this.handleChange = this.handleChange.bind(this);
        this.handleDouble = this.handleDouble.bind(this);
        this.handleNumber = this.handleNumber.bind(this);
        this.handleEqGroup = this.handleEqGroup.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleDate = this.handleDate.bind(this);
        this.handlePlan = this.handlePlan.bind(this);
        this.onChangeRevisionDate = this.onChangeRevisionDate.bind(this);
        this.handleNoWorkPlan = this.handleNoWorkPlan.bind(this);
        this.bindEqGroupIdWiseList = this.bindEqGroupIdWiseList.bind(this);
        this.bindCrewInfo = this.bindCrewInfo.bind(this);
        this.getWorkPlanInfo = this.getWorkPlanInfo.bind(this);
        this.saveCrew = this.saveCrew.bind(this);
        this.validateWorkPlanWithEg = this.validateWorkPlanWithEg.bind(this);
        this.onChangeSearchText = this.onChangeSearchText.bind(this);
    }

    getRules() {
        const isSelectEg = (value) => (value.length > 0);
        const acres = { min: this.unit.toRoundOfArea(1), max: this.unit.toRoundOfArea(50000) };
        const speed = { min: this.unit.toRoundOfSpeed(0.5), max: this.unit.toRoundOfSpeed(50) };

        return [
            {
                field: 'crewName',
                method: 'isEmpty',
                validWhen: false,
                message: 'Crew Name is required.',
                group: 'common'
            },
            {
                field: 'planTypeId',
                method: 'isInt',
                validWhen: true,
                args: [{ min: 1 }],
                validateEmpty: false,
                message: 'Select any one plan type',
                group: "common"
            },
            {
                field: 'selectedEqGroups',
                method: isSelectEg,
                validWhen: true,
                message: 'Select equipment group',
                group: "workplan"
            },
            {
                field: 'plannedAcres',
                method: 'isEmpty',
                validWhen: false,
                message: 'Planned area is required.',
                group: "workplan"
            },
            {
                field: 'plannedAcres',
                method: 'isFloat',
                validWhen: true,
                args: [{ min: acres.min, max: acres.max }],
                validateEmpty: false,
                message: 'Planned area maximum is {max}',
                group: "workplan"
            },
            {
                field: 'utilization',
                method: 'isEmpty',
                validWhen: false,
                message: 'Planned utilization is required.',
                group: "workplan"
            },
            {
                field: 'utilization',
                method: 'isFloat',
                validWhen: true,
                args: [{ min: 1, max: 100 }],
                validateEmpty: false,
                message: 'Planned utilization between {min} and {max}%',
                group: "workplan"
            },
            {
                field: 'avgWorkingSpeed',
                method: 'isEmpty',
                validWhen: false,
                message: 'Planned avg. working speed is required.',
                group: "workplan"
            },
            {
                field: 'avgWorkingSpeed',
                method: 'isFloat',
                validWhen: true,
                args: [{ min: speed.min, max: speed.max }],
                validateEmpty: false,
                message: 'Planned avg. working speed between {min} and {max}',
                group: "workplan"
            },
            {
                field: 'workingHoursPerDay',
                method: 'isEmpty',
                validWhen: false,
                message: 'Planned working hours per day is required.',
                group: "workplan"
            },
            {
                field: 'workingHoursPerDay',
                method: 'isFloat',
                validWhen: true,
                args: [{ min: 1, max: 24 }],
                validateEmpty: false,
                message: 'Planned working hours per day between {min} and {max}',
                group: "workplan"
            },
            {
                field: 'nonWorkingDays',
                method: 'isInt',
                validWhen: true,
                args: [{ min: 0, max: 50 }],
                validateEmpty: false,
                message: 'Planned non-working days maximum is {max}',
                group: "workplan"
            },
            {
                field: 'startDate',
                method: 'isMoment',
                validWhen: true,
                message: 'Please select start date'
            },
            {
                field: 'isNoWorkPlan',
                method: this.validateWorkPlanWithEg,
                validWhen: true,
                message: 'Please add implement equipment group.',
                group: 'common'
            }
        ];
    }

    componentDidMount() {
        this.validator.setValidation(this.getRules());
        const { farmId, id } = getMatchParams(this.props.match.params);
        const crewId = id ? id : null;

        this.returnUrl = `/home/farm/${farmId}/map/crew`;
        this.isEditMode = (crewId !== null);
        this.setState({ crewId, farmId });

        if (this.props.planTypeList.length === 0) this.props.actions.getPlanTypeList();
        if (this.isEditMode) this.props.actions.getCrewInfo(farmId, crewId);
        else this.getCrewEqGroupList(farmId);
    }

    componentDidUpdate(prevProps) {
        if (this.props.crewEquipmentGroups.list !== prevProps.crewEquipmentGroups.list)
            this.bindEqGroupIdWiseList();

        if (this.props.crewInfo && this.props.crewInfo !== prevProps.crewInfo)
            this.bindCrewInfo();
    }

    bindCrewInfo() {
        const c = this.props.crewInfo
        const isNoWorkPlan = !c.is_work_plan;
        const plannedSpec = {
            plannedAcres: this.unit.toArea(parseFloat(c.acres), 2),
            utilization: parseInt(c.utilization),
            avgWorkingSpeed: this.unit.toSpeed(parseFloat(c.avg_working_speed), 2),
            workingHoursPerDay: parseFloat(c.working_hours_per_day),
            nonWorkingDays: parseInt(c.non_working_days)
        };

        if (isNoWorkPlan) this.prevPlanned = this.defaultPlanned;
        let startDate = c.plan_start_date ? toMoment(c.plan_start_date) : null;
        if (startDate && startDate.year() === getDefaultMoment().year()) startDate = null;

        this.setState({
            ...plannedSpec,
            crewName: c.crew_name,
            planTypeId: (c.plan_type_id || 0),
            isNoWorkPlan,
            startDate,
            selectedEqGroups: c.equipment_group_list.map(item => (item.equipment_group_uuid))
        });

        this.props.actions.workPlan({
            total_working_days: c.total_working_days,
            acres_per_day: c.acres_per_day,
            acres_per_hour: c.acres_per_hour,
            equipment_group_list: c.equipment_group_list.map(item => ({
                equipment_group_uuid: item.equipment_group_uuid,
                acres_per_day: item.acres_per_day
            }))
        });

        this.getCrewEqGroupList(this.state.farmId, c.plan_type_id);
    }

    bindEqGroupIdWiseList() {
        const selectedList = [];
        this.eqGroupIdWiseList = {};
        this.props.crewEquipmentGroups.list.forEach((item) => {
            const spec = {};
            if (item.implement.specification && Object.keys(item.implement.specification).length) {
                if (item.implement.specification.hasOwnProperty('rows')) {
                    spec["rows"] = item.implement.specification.rows;
                    spec["row_spacing"] = item.implement.specification.row_spacing;
                } else if (item.implement.specification.hasOwnProperty('width')) {
                    spec["width"] = item.implement.specification.width;
                }
            }

            this.eqGroupIdWiseList[item.equipment_group_uuid] = {
                equipment_group_uuid: item.equipment_group_uuid,
                specification: Object.keys(spec).length > 0 ? spec : null
            };

            if (this.state.selectedEqGroups.indexOf(item.equipment_group_uuid) >= 0)
                selectedList.push(item.equipment_group_uuid);
        });

        this.setState({ selectedEqGroups: selectedList });
    }

    handleChange(event) {
        const { name, value } = event.target;
        this.setState({ [name]: value });
    }

    handleDouble(event) {
        const { name, value } = event.target;
        let onlyNums = value.replace(/[^0-9.]/g, '').toString();
        this.setState({ [name]: onlyNums });
    }

    handleNumber(event) {
        const { name, value } = event.target;
        const onlyNums = value.replace(/[^0-9]/g, '');
        this.setState({ [name]: onlyNums });
    }

    handleDate(keyName, value) {
        this.setState({ [keyName]: value });
    }

    onChangeRevisionDate(e) {
        this.setState({ revisionDate: e ? e.value : '' });
    }

    handleEqGroup(eqGroupId, e) {
        const { checked } = e.target;
        let selectedEqGroups = this.state.selectedEqGroups;

        if (checked && selectedEqGroups.indexOf(eqGroupId) < 0) {
            selectedEqGroups.push(eqGroupId);
            this.setState({ selectedEqGroups });
        }
        else if (!checked && selectedEqGroups.indexOf(eqGroupId) >= 0) {
            selectedEqGroups.splice(selectedEqGroups.indexOf(eqGroupId), 1);
            this.setState({ selectedEqGroups });
        }
        this.getWorkPlanInfo();
    }

    handleBlur() {
        this.getWorkPlanInfo();
    }

    handlePlan(planTypeId) {
        if (planTypeId === this.state.planTypeId) return;
        this.submitted = false;
        this.setState({ planTypeId, selectedEqGroups: [], validation: this.validator.valid() });
        this.props.actions.workPlan();
        this.getCrewEqGroupList(this.state.farmId, planTypeId);
    }

    handleNoWorkPlan(e) {
        const { checked, name } = e.target;
        if (checked) {
            this.prevPlanned = {
                plannedAcres: this.state.plannedAcres,
                utilization: this.state.utilization,
                avgWorkingSpeed: this.state.avgWorkingSpeed,
                workingHoursPerDay: this.state.workingHoursPerDay,
                nonWorkingDays: this.state.nonWorkingDays
            }
            this.setState({ [name]: checked, ...this.clearPlanned, validateGroup: 'common' });
        }
        else {
            this.setState({ [name]: checked, ...this.prevPlanned });
            this.getWorkPlanInfo();
        }
    }

    getWorkPlanInfo() {
        if (this.state.isNoWorkPlan) return;
        if (this.isValidateWorkPlan() && this.state.selectedEqGroups.length > 0) {
            const s = this.state;
            const params = {
                farm_uuid: s.farmId,
                plan_type_id: s.planTypeId,
                acres: this.unit.fromArea(parseFloat(s.plannedAcres)),
                utilization: parseFloat(s.utilization),
                avg_working_speed: this.unit.fromSpeed(parseFloat(s.avgWorkingSpeed)),
                working_hours_per_day: parseFloat(s.workingHoursPerDay),
                non_working_days: Number(s.nonWorkingDays) ? parseInt(s.nonWorkingDays) : 0,
                equipment_group_list: s.selectedEqGroups.map((eqGroupId) => {
                    return { ...this.eqGroupIdWiseList[eqGroupId] };
                })
            }
            this.props.actions.getWorkPlan(params);
        } else this.props.actions.workPlan();
    }

    isValidateWorkPlan() {
        const validateGroup = "workplan";
        const validation = this.validator.validate(this.state, validateGroup);
        this.setState({ validation, validateGroup });
        this.submitted = true;
        return validation.isValid;
    }

    getCrewEqGroupList(farmId, planTypeId) {
        farmId = farmId || this.state.farmId;
        planTypeId = planTypeId || this.state.planTypeId;
        if (planTypeId > 0) this.props.actions.getCrewEquipmentGroupList({ farmId, planTypeId });
    }

    onChangeSearchText(e) {
        this.setState({ searchText: e.target.value });
    }

    validateWorkPlanWithEg() {
        if (this.state.selectedEqGroups.length === 0) return true;

        const nonTrucks = this.props.crewEquipmentGroups.list
            .filter(e => this.state.selectedEqGroups.indexOf(e.equipment_group_uuid) >= 0 && !e.vehicle.is_truck);

        if (!this.state.isNoWorkPlan) return (nonTrucks.length > 0);
        return true;
    }

    saveCrew() {
        const s = this.state;
        const validateGroup = s.isNoWorkPlan ? 'common' : null;
        const validation = this.validator.validate(s, validateGroup);
        this.setState({ validation, validateGroup });
        this.submitted = true;

        if (validation.isValid) {
            const success = () => this.props.history.push(this.returnUrl);

            const params = {
                farm_uuid: s.farmId,
                plan_type_id: s.planTypeId,
                crew_name: s.crewName,
                is_work_plan: !(s.isNoWorkPlan),
                acres: this.unit.fromArea(parseFloat(s.plannedAcres)),
                utilization: parseFloat(s.utilization),
                avg_working_speed: this.unit.fromSpeed(parseFloat(s.avgWorkingSpeed)),
                working_hours_per_day: parseFloat(s.workingHoursPerDay),
                non_working_days: Number(s.nonWorkingDays) ? parseInt(s.nonWorkingDays) : 0,
                plan_start_date: s.startDate ? toApiDateString(s.startDate) : toApiDateString(getDefaultMoment()),
                equipment_group_list: s.selectedEqGroups
            }

            if (!this.isEditMode) this.props.actions.createCrew(params, success);
            else {
                params.crew_uuid = this.state.crewId;
                this.props.actions.editCrew(params, success);
            }
        }
    }

    componentWillUnmount() {
        this.props.actions.crewInfo();
        this.props.actions.workPlan();
        this.props.actions.crewEquipmentGroupList();
    }

    render() {
        const s = this.state;
        const validateGroup = s.isNoWorkPlan ? 'common' : null;
        const v = this.submitted ? this.validator.validate(this.state, validateGroup) : this.state.validation;
        const settings = this.props.settings;
        const p = this.props.workPlanInfo
        const unit = this.unit;
        const noWorkPlanProps = s.isNoWorkPlan ? { defaultChecked: true } : {};

        return (
            <CardContainer lg={10} md={10} className="crew-form">
                <Card className="card-form">
                    <Card.Header>
                        <Card.Title>
                            <Heading5>
                                <TitleIcon className="fa fa-users" />{this.isEditMode ? 'Edit Crew' : 'Add Crew'}
                            </Heading5>
                        </Card.Title>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col lg={6} md={6}>
                                <Row className="summary-crew">
                                    <Col lg={4} md={4}>
                                        <h6>Planned Working Days</h6>
                                        <hr />
                                        <div className="text-center summary-val">{roundOfDays(p.total_working_days)}</div>
                                    </Col>
                                    <Col lg={4} md={4}>
                                        <h6>Planned {settings.areaShortName}/day</h6>
                                        <hr />
                                        <div className="text-center summary-val">{unit.toRoundOfArea(p.acres_per_day)}</div>
                                    </Col>
                                    <Col lg={4} md={4}>
                                        <h6>Planned {settings.areaShortName}/hour</h6>
                                        <hr />
                                        <div className="text-center summary-val">{unit.toRoundOfArea(p.acres_per_hour)}</div>
                                    </Col>
                                </Row>
                                <Row className={`no-operation mx-0 ${s.isNoWorkPlan ? 'active' : ''}`}>
                                    <Col md={11} className="pr-0">
                                        <b>No work plan for this crew</b><br />
                                        (actual work is always tracked)
                                    </Col>
                                    <Col md={1}>
                                        <input type="checkbox" {...noWorkPlanProps} name='isNoWorkPlan' onClick={this.handleNoWorkPlan} className="float-right ml-2 mt-2" />
                                    </Col>
                                </Row>
                                <CrewPlanType planTypeList={this.props.planTypeList} activePlanTypeId={s.planTypeId}
                                    disabled={this.isEditMode} onSelect={this.handlePlan}>
                                    <Validator.Text validation={v.planTypeId} />
                                </CrewPlanType>
                                <Row>
                                    <Col>
                                        <Form.Group>
                                            <Validator validation={v.crewName}>
                                                <Form.Label htmlFor="txtCrewName">Crew Name<Validator.Mand /></Form.Label>
                                                <Form.Control type="text" id="txtCrewName" name="crewName" placeholder="Enter the crew name"
                                                    maxLength='50' value={s.crewName} onChange={this.handleChange} autoComplete="off" />
                                                <Validator.Text />
                                            </Validator>
                                        </Form.Group>
                                        <div className={s.isNoWorkPlan ? 'event-disabled' : ''}>
                                            <Form.Group>
                                                <Validator validation={v.plannedAcres}>
                                                    <Form.Label htmlFor="txtPlannedArea">Planned {settings.areaShortName}<Validator.Mand /></Form.Label>
                                                    <InputGroup>
                                                        <Form.Control type="text" id="txtPlannedAcres" name="plannedAcres" placeholder={`Enter the planned ${settings.areaShortName}`}
                                                            onChange={this.handleDouble} onBlur={this.handleBlur} maxLength='20' value={s.plannedAcres} autoComplete="off" />
                                                        <InputGroup.Append>
                                                            <InputGroup.Text>{settings.areaShortName}</InputGroup.Text>
                                                        </InputGroup.Append>
                                                    </InputGroup>
                                                    <Validator.Text />
                                                </Validator>
                                            </Form.Group>
                                            <Form.Group>
                                                <Validator validation={v.utilization}>
                                                    <Form.Label htmlFor="txtPlannedArea">Planned Utilization<Validator.Mand /></Form.Label>
                                                    <InputGroup>
                                                        <Form.Control type="text" id="txtUtilization" name="utilization" placeholder="Enter the utilization"
                                                            onChange={this.handleDouble} onBlur={this.handleBlur} maxLength='5' value={s.utilization} autoComplete="off" />
                                                        <InputGroup.Append>
                                                            <InputGroup.Text>%</InputGroup.Text>
                                                        </InputGroup.Append>
                                                    </InputGroup>
                                                    <Validator.Text />
                                                </Validator>
                                            </Form.Group>
                                            <Form.Group>
                                                <Validator validation={v.avgWorkingSpeed}>
                                                    <Form.Label htmlFor="txtAvgWorkingSpeed">Planned Avg. Working Speed<Validator.Mand /></Form.Label>
                                                    <InputGroup>
                                                        <Form.Control type="text" id="txtAvgWorkingSpeed" name="avgWorkingSpeed" placeholder="Enter the avg. working speed"
                                                            onChange={this.handleDouble} onBlur={this.handleBlur} maxLength='5' value={s.avgWorkingSpeed} autoComplete="off" />
                                                        <InputGroup.Append>
                                                            <InputGroup.Text>{settings.speedShortName}</InputGroup.Text>
                                                        </InputGroup.Append>
                                                    </InputGroup>
                                                    <Validator.Text />
                                                </Validator>
                                            </Form.Group>
                                            <Form.Group>
                                                <Validator validation={v.workingHoursPerDay}>
                                                    <Form.Label htmlFor="txtWorkingHoursPerDay">Planned Working Hours Per Day<Validator.Mand /></Form.Label>
                                                    <InputGroup>
                                                        <Form.Control type="text" id="txtWorkingHoursPerDay" name="workingHoursPerDay" placeholder="Enter the working hours per day"
                                                            onChange={this.handleDouble} onBlur={this.handleBlur} maxLength='3' value={s.workingHoursPerDay} autoComplete="off" />
                                                        <InputGroup.Append>
                                                            <InputGroup.Text>Hours</InputGroup.Text>
                                                        </InputGroup.Append>
                                                    </InputGroup>
                                                    <Validator.Text />
                                                </Validator>
                                            </Form.Group>
                                            <Form.Group>
                                                <Validator validation={v.nonWorkingDays}>
                                                    <Form.Label htmlFor="txtNonWorkingDays">Planned Non-Working Days<Validator.Mand /></Form.Label>
                                                    <InputGroup>
                                                        <Form.Control type="text" id="txtNonWorkingDays" name="nonWorkingDays" placeholder="Enter the non-working days"
                                                            onChange={this.handleNumber} onBlur={this.handleBlur} maxLength='3' value={s.nonWorkingDays} autoComplete="off" />
                                                        <InputGroup.Append>
                                                            <InputGroup.Text>Days</InputGroup.Text>
                                                        </InputGroup.Append>
                                                    </InputGroup>
                                                    <Validator.Text />
                                                </Validator>
                                            </Form.Group>
                                            <Form.Group>
                                                <Validator validation={v.startDate}>
                                                    <Form.Label htmlFor="dpStartDate">Start Date<Validator.Mand /></Form.Label>
                                                    <Datetime id="dpStartDate" placeholder="Select start date" name="startDate" closeOnSelect={true}
                                                        dateFormat={DISPLAY_DATE_FORMAT} timeFormat={false} onChange={this.handleDate.bind(this, "startDate")}
                                                        value={this.state.startDate} selected={this.state.startDate} autoComplete="off" />
                                                    <Validator.Text />
                                                </Validator>
                                            </Form.Group>
                                        </div>
                                    </Col>
                                </Row>
                            </Col>
                            <Col lg={6} md={6}>
                                {!v.isNoWorkPlan.isValid && v.isNoWorkPlan.message &&
                                    <Alert variant="danger">
                                        {v.isNoWorkPlan.message}
                                    </Alert>}
                                <CrewEqGroupList equipmentGroupList={this.props.crewEquipmentGroups.list}
                                    isFetching={this.props.crewEquipmentGroups.isFetching}
                                    isEditMode={this.isEditMode}
                                    selectedList={s.selectedEqGroups}
                                    workPlanEqGroupList={this.props.workPlanInfo.equipment_group_list}
                                    currentCrewId={s.crewId} onSelect={this.handleEqGroup}
                                    searchText={s.searchText}
                                    onChangeSearchText={this.onChangeSearchText}
                                    unit={this.unit} {...{ settings }}>
                                    <Validator.Text validation={v.selectedEqGroups} />
                                </CrewEqGroupList>
                            </Col>
                        </Row>
                    </Card.Body>
                    <Card.Footer>
                        <Button variant="primary" size="sm" className="float-right mx-1" onClick={this.saveCrew}>
                            <i className="fa fa-floppy-o"></i> Save
                        </Button>
                        <NavLink to={this.returnUrl}>
                            <Button variant="danger" size="sm" className="float-right mx-1">
                                <i className="fa fa-times-circle"></i> Cancel
                            </Button>
                        </NavLink>
                    </Card.Footer>
                </Card>
            </CardContainer>);
    }
}

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        getPlanTypeList,
        crewEquipmentGroupList,
        getCrewEquipmentGroupList,
        workPlan,
        getWorkPlan,
        getCrewInfo,
        crewInfo,
        createCrew,
        editCrew
    }, dispatch)
});

const mapStateToProps = state => ({
    settings: state.user.settings,
    planTypeList: state.farm.planTypeList,
    crewEquipmentGroups: state.equipmentGroup.crewEquipmentGroups,
    workPlanInfo: state.crew.workPlanInfo,
    crewInfo: state.crew.crewInfo
});

export default connect(mapStateToProps, mapDispatchToProps)(CrewForm);